import Link from "next/link"
import type { LucideIcon } from "lucide-react"
import type { MouseEventHandler, ReactNode } from "react"

/**
 * The site's CTA pair — the landing hero buttons. Both carry the same premium
 * hover: a specular streak (`.cta-sheen`) that glides across once, over a
 * gloss (`.cta-gloss`) that deepens with it (globals.css).
 *
 * Colours come from the glass tokens (`--pg-*`, painted by GlassSurface).
 * Off a glass surface the fallbacks read the app theme instead, so the same
 * pair works on token pages like /rewards: primary is foreground-on-
 * background (black pill in light, white in dark), secondary is a card.
 */
type HeroCtaProps = {
  href: string
  children: ReactNode
  /** Trailing icon — ArrowRight on the landing primary, Plus on "Post an opening". */
  icon?: LucideIcon
  onClick?: MouseEventHandler<HTMLAnchorElement>
  className?: string
  /** "compact" sits beside the slim search pill on the barber hero, where the
      full 48px pair looked heavy under a 30px field. */
  size?: "default" | "compact"
}

const BASE =
  "group relative isolate inline-flex items-center justify-center overflow-hidden rounded-full font-medium"
const SIZE = {
  default: "h-12 gap-2 px-7 text-[15px]",
  compact: "h-9 gap-1.5 px-4 text-[13px]",
}

export function HeroCtaPrimary({ href, children, icon: Icon, onClick, className = "", size = "default" }: HeroCtaProps) {
  return (
    <Link
      href={href}
      onClick={onClick}
      className={`${BASE} ${SIZE[size]} transition-transform duration-200 hover:scale-[1.02] active:scale-95 ${className}`}
      style={{
        background: "var(--pg-invert-bg, var(--foreground))",
        color: "var(--pg-invert-fg, var(--background))",
        boxShadow: "0 18px 40px -20px rgba(0,0,0,0.9)",
        ["--cta-sheen" as string]: "var(--pg-sheen-solid, rgba(255,255,255,0.38))",
      }}
    >
      {/* Hover layers in their own clipped shell. On a GPU-composited hover the
          button's overflow-hidden rounding can drop out and the gloss/sheen
          paint as a square; clip-path holds the pill shape regardless, and
          sits here rather than on the button so the drop shadow survives. */}
      <span aria-hidden className="pointer-events-none absolute inset-0" style={{ clipPath: "inset(0 round 9999px)" }}>
      <span
        aria-hidden
        className="cta-gloss pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.03) 40%, rgba(0,0,0,0.20) 100%)",
          mixBlendMode: "overlay",
        }}
      />
      <span aria-hidden className="cta-sheen pointer-events-none absolute inset-y-0 w-1/3" />
      </span>
      <span className="relative z-10">{children}</span>
      {Icon && (
        <Icon
          className={`relative z-10 transition-transform duration-200 group-hover:translate-x-0.5 ${size === "compact" ? "h-3.5 w-3.5" : "h-4 w-4"}`}
        />
      )}
    </Link>
  )
}

export function HeroCtaSecondary({ href, children, icon: Icon, onClick, className = "", size = "default" }: HeroCtaProps) {
  return (
    <Link
      href={href}
      onClick={onClick}
      className={`${BASE} ${SIZE[size]} backdrop-blur-xl transition-colors duration-200 ${className}`}
      style={{
        background: "var(--pg-card, var(--card))",
        border: "1px solid var(--pg-card-edge, var(--border))",
        color: "var(--pg-fg, var(--foreground))",
        // Its backdrop blur is the other square: no outer shadow here, so the
        // whole button can carry the rounded clip.
        clipPath: "inset(0 round 9999px)",
        ["--cta-sheen" as string]: "var(--pg-sheen-glass, rgba(20,18,15,0.14))",
      }}
    >
      {/* Hover layers in their own clipped shell. On a GPU-composited hover the
          button's overflow-hidden rounding can drop out and the gloss/sheen
          paint as a square; clip-path holds the pill shape regardless, and
          sits here rather than on the button so the drop shadow survives. */}
      <span aria-hidden className="pointer-events-none absolute inset-0" style={{ clipPath: "inset(0 round 9999px)" }}>
      <span
        aria-hidden
        className="cta-gloss pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.02) 44%, rgba(0,0,0,0.16) 100%)",
          mixBlendMode: "overlay",
        }}
      />
      <span aria-hidden className="cta-sheen pointer-events-none absolute inset-y-0 w-1/3" />
      </span>
      <span className="relative z-10">{children}</span>
      {Icon && <Icon className={`relative z-10 ${size === "compact" ? "h-3.5 w-3.5" : "h-4 w-4"}`} />}
    </Link>
  )
}

/**
 * The primary CTA as a real <button>.
 *
 * Same pill, same gloss and sheen as HeroCtaPrimary — this exists because the
 * pair above are <Link>s, and a form's submit has an onClick, a disabled state
 * and a spinner. Styling lives in one place so the signup CTA and the landing
 * CTA cannot drift apart.
 *
 * CALLERS OFF A GLASS SURFACE MUST PIN THE COLOURS. The --pg-* tokens are
 * painted by GlassSurface; without one they fall back to --foreground /
 * --background, which follow the APP theme. On a permanently-dark surface like
 * AuthPageShell a light-mode viewer would then get a near-black pill on a
 * near-black ground. Pass invert={{ bg, fg }} there.
 */
export function HeroCtaButton({
  children,
  icon: Icon,
  onClick,
  className = "",
  size = "default",
  disabled = false,
  type = "button",
  invert,
}: Omit<HeroCtaProps, "href" | "onClick"> & {
  onClick?: MouseEventHandler<HTMLButtonElement>
  disabled?: boolean
  type?: "button" | "submit"
  /** Explicit pill colours, for surfaces that don't paint the --pg-* tokens. */
  invert?: { bg: string; fg: string }
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${BASE} ${SIZE[size]} w-full transition-transform duration-200 hover:scale-[1.02] active:scale-95 disabled:pointer-events-none disabled:opacity-60 ${className}`}
      style={{
        background: invert?.bg ?? "var(--pg-invert-bg, var(--foreground))",
        color: invert?.fg ?? "var(--pg-invert-fg, var(--background))",
        boxShadow: "0 18px 40px -20px rgba(0,0,0,0.9)",
        ["--cta-sheen" as string]: "var(--pg-sheen-solid, rgba(255,255,255,0.38))",
      }}
    >
      <span aria-hidden className="pointer-events-none absolute inset-0" style={{ clipPath: "inset(0 round 9999px)" }}>
        <span
          aria-hidden
          className="cta-gloss pointer-events-none absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.03) 40%, rgba(0,0,0,0.20) 100%)",
            mixBlendMode: "overlay",
          }}
        />
        <span aria-hidden className="cta-sheen pointer-events-none absolute inset-y-0 w-1/3" />
      </span>
      <span className="relative z-10 inline-flex items-center gap-2">{children}</span>
      {Icon && (
        <Icon
          className={`relative z-10 transition-transform duration-200 group-hover:translate-x-0.5 ${size === "compact" ? "h-3.5 w-3.5" : "h-4 w-4"}`}
        />
      )}
    </button>
  )
}
